import { Analytics } from './analytics'
import { Api } from './api'
import { Launcher } from './launcher'
import { Panel } from './panel'
import { styles } from './styles'
import type { Bootstrap, RuntimeConfig } from './types'

/**
 * Entry point. Mounts the widget into a Shadow DOM so the host theme's CSS cannot reach in
 * and the widget's styles cannot leak out — worth the small extra complexity on a
 * WordPress site with a theme and plugins nobody here controls.
 *
 * Everything is best-effort: if the API is unreachable or nothing has been published, the
 * widget quietly does not appear. A broken chat bubble on the homepage is worse than none.
 */

const MOUNT_ID = 'gvfb-chatbot-root'

async function boot(): Promise<void> {
  const config = (window as typeof window & { GVFB_CHATBOT_CONFIG?: RuntimeConfig })
    .GVFB_CHATBOT_CONFIG

  if (!config?.apiUrl) {
    return
  }

  if (document.getElementById(MOUNT_ID)) {
    return // Already mounted — a second enqueue of the script must not double it up.
  }

  const api = new Api(config)

  let data: Bootstrap

  try {
    data = await api.bootstrap()
  } catch {
    return
  }

  const settings = {
    ...data.settings,
    ...(config.position ? { position: config.position } : {}),
    ...(config.pulse === false ? { pulse_enabled: false } : {}),
    ...(config.greeting ? { launcher_text: config.greeting } : {}),
  }

  const host = document.createElement('div')
  host.id = MOUNT_ID
  const shadow = host.attachShadow({ mode: 'open' })

  const sheet = document.createElement('style')
  sheet.textContent = styles(settings)

  const root = document.createElement('div')
  root.className = 'root'

  const analytics = new Analytics(api, settings)

  let panel: Panel | null = null

  const launcher = new Launcher(settings, () => {
    void open()
  })

  const close = (): void => {
    panel?.element().setAttribute('hidden', '')
    launcher.setOpen(false)
    analytics.track({ type: 'close' })
    // Return focus to the control that opened the dialog, or a keyboard visitor is
    // stranded at the top of the document.
    launcher.focus()
  }

  const open = async (): Promise<void> => {
    // The session is opened on first use, not on page load: a visitor who never clicks
    // should not create a row, and it keeps the analytics honest about real engagement.
    if (!api.hasSession()) {
      await api.openSession()
    }

    panel ??= (() => {
      const created = new Panel({ ...data, settings }, api, analytics, close)

      root.append(created.element())

      return created
    })()

    panel.element().removeAttribute('hidden')
    launcher.setOpen(true)
    analytics.track({ type: 'open' })
    panel.start()
  }

  root.append(launcher.element())
  shadow.append(sheet, root)
  document.body.append(host)

  launcher.maybePulse()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => void boot())
} else {
  void boot()
}
